import type { Color } from "@core/colors";

/**
 * Rubber Nuts and Bolts — hand-authored levels.
 *
 * The play field is a regular grid of holes; pins sit in holes; bands are
 * anchored to a list of holes (referenced by row/col coordinates). A band
 * releases (falls into its color box) the moment NO pin sits in any of its
 * anchor holes — i.e. the player has rerouted every pin off of it.
 *
 * The "moving-pin" mechanic: tapping a pin slides it into the single free
 * hole. The free hole then moves to the source.
 *
 * SOLVABILITY INVARIANT (important!): with K initial free holes, the
 * maximum number of holes that can be empty SIMULTANEOUSLY is K. So a band
 * with K anchors needs all K positions free at once — and a band with
 * MORE than K anchors is mathematically unreleasable. Every level below
 * obeys: every band's anchor-count is ≤ number of free holes in `freeHoles`.
 *
 * Field layout (see main.ts `spawnLevel`):
 *  - `cols` × `rows` holes arranged in a regular grid.
 *  - `freeHoles`  : list of (col,row) coordinates that start EMPTY.
 *  - `bands`      : each band lists its color + the (col,row) anchor holes.
 *
 * Hole coordinates: col 0 is leftmost, row 0 is topmost.
 *
 * Designer notes / progression:
 *  - L1 (3×3): 2 free holes, 3 short 2-anchor bands. Obvious moves.
 *  - L2 (3×3): 2 free holes, 4 interleaved 2-anchor bands. Order matters.
 *  - L3 (4×3): 2 free holes, 4 bands. Wider field, more routing options.
 *  - L4 (4×4): 3 free holes, 5 bands including one 3-anchor band.
 *  - L5 (4×4): 3 free holes, 6 bands including one 3-anchor band; densest.
 *
 * NOTE: the moving-pin mechanic makes >2-anchor bands fiddly even when
 * solvable — the player has to keep all anchors of a band free at the
 * release-check moment, which is harder than it sounds. See the commit
 * report for caveats.
 */
export interface HoleCoord {
  col: number;
  row: number;
}

export interface BandSpecNB {
  color: Color;
  holes: HoleCoord[];
}

export interface NutsAndBoltsLevel {
  cols: number;
  rows: number;
  /** Coordinates of holes that start without a pin. At least 1 free hole. */
  freeHoles: HoleCoord[];
  bands: BandSpecNB[];
}

// L1 — 3×3, 2 free holes, 3 short 2-anchor bands. Each band can release
// after vacating its two specific anchor holes. The intent is "obvious in
// 2-3 taps each": every band's anchors are adjacent, so the free-hole walk
// to vacate them is short.
const L1: NutsAndBoltsLevel = {
  cols: 3,
  rows: 3,
  freeHoles: [{ col: 2, row: 2 }, { col: 0, row: 2 }],
  bands: [
    { color: "red", holes: [{ col: 0, row: 0 }, { col: 1, row: 0 }] },
    { color: "blue", holes: [{ col: 2, row: 0 }, { col: 2, row: 1 }] },
    { color: "yellow", holes: [{ col: 0, row: 1 }, { col: 1, row: 1 }] },
  ],
};

// L2 — 3×3, 2 free holes, 4 bands. Bands interleave so naive top-down play
// can get stuck; player has to think about which band's anchors to vacate
// first.
const L2: NutsAndBoltsLevel = {
  cols: 3,
  rows: 3,
  freeHoles: [{ col: 2, row: 2 }, { col: 0, row: 2 }],
  bands: [
    { color: "red", holes: [{ col: 0, row: 0 }, { col: 1, row: 0 }] },
    { color: "blue", holes: [{ col: 2, row: 0 }, { col: 2, row: 1 }] },
    { color: "yellow", holes: [{ col: 0, row: 1 }, { col: 1, row: 2 }] },
    { color: "green", holes: [{ col: 1, row: 1 }, { col: 1, row: 2 }] },
  ],
};

// L3 — 4×3, 2 free holes, 4 bands all 2-anchor. More interleaving.
const L3: NutsAndBoltsLevel = {
  cols: 4,
  rows: 3,
  freeHoles: [{ col: 3, row: 2 }, { col: 0, row: 2 }],
  bands: [
    { color: "red", holes: [{ col: 0, row: 0 }, { col: 1, row: 0 }] },
    { color: "blue", holes: [{ col: 2, row: 0 }, { col: 3, row: 0 }] },
    { color: "yellow", holes: [{ col: 0, row: 1 }, { col: 2, row: 1 }] },
    { color: "green", holes: [{ col: 1, row: 1 }, { col: 3, row: 1 }] },
  ],
};

// L4 — 4×4, 3 free holes, 5 bands including one 3-anchor band. The
// 3-anchor band can release because with 3 free holes there's room to
// vacate all three of its anchor positions simultaneously.
const L4: NutsAndBoltsLevel = {
  cols: 4,
  rows: 4,
  freeHoles: [{ col: 3, row: 3 }, { col: 0, row: 3 }, { col: 3, row: 2 }],
  bands: [
    { color: "red", holes: [{ col: 0, row: 0 }, { col: 1, row: 0 }] },
    { color: "blue", holes: [{ col: 2, row: 0 }, { col: 3, row: 0 }] },
    { color: "yellow", holes: [{ col: 0, row: 1 }, { col: 1, row: 1 }] },
    { color: "green", holes: [{ col: 2, row: 1 }, { col: 1, row: 2 }] },
    { color: "purple", holes: [{ col: 0, row: 2 }, { col: 2, row: 2 }, { col: 1, row: 3 }] },
  ],
};

// L5 — 4×4, 3 free holes, 6 bands. One 3-anchor band (purple), rest are
// 2-anchor. Sole free hole would only allow 1-anchor bands; 3 free holes
// support up to 3-anchor bands.
const L5: NutsAndBoltsLevel = {
  cols: 4,
  rows: 4,
  freeHoles: [{ col: 3, row: 3 }, { col: 0, row: 0 }, { col: 3, row: 0 }],
  bands: [
    { color: "red", holes: [{ col: 1, row: 0 }, { col: 2, row: 0 }] },
    { color: "blue", holes: [{ col: 3, row: 1 }, { col: 2, row: 1 }] },
    { color: "yellow", holes: [{ col: 0, row: 1 }, { col: 0, row: 2 }] },
    { color: "green", holes: [{ col: 1, row: 1 }, { col: 2, row: 2 }, { col: 1, row: 2 }] },
    { color: "purple", holes: [{ col: 1, row: 3 }, { col: 2, row: 3 }] },
    { color: "orange", holes: [{ col: 0, row: 3 }, { col: 3, row: 2 }] },
  ],
};

export const levels: NutsAndBoltsLevel[] = [L1, L2, L3, L4, L5];
